import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout } from '../../components/Layout/Layout';
import { api } from '../../services/api';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import styles from './Statistiques.module.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Overview {
  nbCommandes: number; caTotal: number; panierMoyen: number;
  nbAvecReduction: number; pctAvecReduction: number; nbClientsUniques: number;
}
interface CaJour      { date: string; nbCommandes: number; ca: number; }
interface TopProduit  { nom: string; quantite: number; ca: number; }
interface Livraison   { mode: string; count: number; ca: number; }
interface StatutItem  { statut: string; count: number; }
interface Ville       { ville: string; count: number; ca: number; }
interface Conversion  { total: number; paye: number; enAttente: number; echec: number; tauxConversion: number; }
interface HeureStat   { heure: number; count: number; ca: number; }
interface Promo       { code: string; count: number; ca: number; }
interface ClientEvo   { date: string; nbClients: number; }

// ─── Constants ───────────────────────────────────────────────────────────────

const ORDER_KEY   = 'stats_order_v3';
const VISIBLE_KEY = 'stats_visible_v3';
// ─── TODO: Looker Studio ──────────────────────────────────────────────────────
// On a créé un nouveau rapport Looker Studio vide connecté à eliquidesud.com (GA4 | 459897354)
// URL du rapport en cours de construction :
//   https://lookerstudio.google.com/reporting/6dd19894-a01c-42ff-ba26-4fdc32e5661c/page/c71rF
// Prochaines étapes :
//   1. Finir de construire le rapport Looker Studio (ajouter les graphiques : série temporelle, tableau pages vues, etc.)
//   2. Activer "Intégrer le rapport" (Fichier → Intégrer le rapport → cocher "Activer l'intégration")
//   3. Rendre le rapport public (Partager → Publique)
//   4. Mettre à jour GA4_REPORT_ID avec le nouvel ID (6dd19894-a01c-42ff-ba26-4fdc32e5661c)
//   5. L'ancien rapport (3e74a590...) était un template GA4 sample, ne pas l'utiliser
// ─────────────────────────────────────────────────────────────────────────────
const GA4_REPORT_ID = '3e74a590-fafc-4b63-9cc6-ada81b3f6538'; // TODO: remplacer par 6dd19894-a01c-42ff-ba26-4fdc32e5661c une fois le rapport terminé

const ALL_WIDGETS = [
  { id: 'kpis',       label: 'KPIs principaux',    icon: '📊' },
  { id: 'evolution',  label: 'Évolution CA / jour', icon: '📈' },
  { id: 'clients',    label: 'Évolution clients',   icon: '👥' },
  { id: 'conversion', label: 'Taux de conversion',  icon: '🎯' },
  { id: 'heures',     label: 'Activité par heure',  icon: '⏰' },
  { id: 'produits',   label: 'Top produits',        icon: '📦' },
  { id: 'livraisons', label: 'Modes de livraison',  icon: '🚚' },
  { id: 'statuts',    label: 'Statuts commandes',   icon: '🔖' },
  { id: 'promos',     label: 'Codes promo',         icon: '🎟️' },
  { id: 'villes',     label: 'Top villes',          icon: '📍' },
];

const PIE_COLORS = ['#4299e1','#48bb78','#ed8936','#9f7aea','#38b2ac','#f56565','#ecc94b'];

const STATUT_LABELS: Record<string,string> = {
  paye:'Payé', en_attente:'En attente', echec:'Échec', rembourse:'Remboursé',
  en_preparation:'En préparation', expedie:'Expédié', livre:'Livré', inconnu:'Inconnu',
};
const MODE_LABELS: Record<string,string> = {
  mondial_relay:'Mondial Relay', colissimo:'Colissimo', non_defini:'Non défini',
};

type Preset = '7j'|'30j'|'3m'|'1an'|'custom';

function getPresetDates(p: Preset) {
  const to = new Date(); const from = new Date();
  if (p==='7j')  from.setDate(to.getDate()-7);
  if (p==='30j') from.setDate(to.getDate()-30);
  if (p==='3m')  from.setMonth(to.getMonth()-3);
  if (p==='1an') from.setFullYear(to.getFullYear()-1);
  return { from: from.toISOString().slice(0,10), to: to.toISOString().slice(0,10) };
}

function getCompareDates(from: string, to: string) {
  const f = new Date(from); const t = new Date(to);
  const dur = t.getTime() - f.getTime();
  return {
    from: new Date(f.getTime()-dur).toISOString().slice(0,10),
    to:   new Date(t.getTime()-dur).toISOString().slice(0,10),
  };
}

const fmt = (n: number) => new Intl.NumberFormat('fr-FR',{style:'currency',currency:'EUR'}).format(n);
const fmtShort = (d: string) => { const dt=new Date(d); return `${String(dt.getDate()).padStart(2,'0')}/${String(dt.getMonth()+1).padStart(2,'0')}`; };
const pct = (a: number, b: number) => b>0 ? Math.round((a-b)/b*100) : null;

// ─── Component ───────────────────────────────────────────────────────────────

export const Statistiques: React.FC = () => {

  // ── Period
  const [preset, setPreset]     = useState<Preset>('30j');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo,   setDateTo]   = useState('');

  // ── Comparison
  const [compareOn, setCompareOn]         = useState(false);
  const [compareOv, setCompareOv]         = useState<Overview|null>(null);

  // ── Widget management
  const [widgetOrder, setWidgetOrder] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(ORDER_KEY)||'null') || ALL_WIDGETS.map(w=>w.id); }
    catch { return ALL_WIDGETS.map(w=>w.id); }
  });
  const [visible, setVisible] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(VISIBLE_KEY)||'null') || ALL_WIDGETS.map(w=>w.id); }
    catch { return ALL_WIDGETS.map(w=>w.id); }
  });
  const [showPanel, setShowPanel] = useState(false);

  // ── Data
  const [overview,   setOverview]   = useState<Overview|null>(null);
  const [caJour,     setCaJour]     = useState<CaJour[]>([]);
  const [clients,    setClients]    = useState<ClientEvo[]>([]);
  const [conversion, setConversion] = useState<Conversion|null>(null);
  const [heures,     setHeures]     = useState<HeureStat[]>([]);
  const [produits,   setProduits]   = useState<TopProduit[]>([]);
  const [livraisons, setLivraisons] = useState<Livraison[]>([]);
  const [statuts,    setStatuts]    = useState<{paiement:StatutItem[];livraison:StatutItem[]}|null>(null);
  const [promos,     setPromos]     = useState<Promo[]>([]);
  const [villes,     setVilles]     = useState<Ville[]>([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  const orderedVisible = useMemo(()=> widgetOrder.filter(id=>visible.includes(id)), [widgetOrder,visible]);

  // ── Init preset dates
  useEffect(()=>{ if(preset!=='custom'){ const d=getPresetDates(preset); setDateFrom(d.from); setDateTo(d.to); } },[preset]);

  // ── Load all data
  const loadAll = useCallback(async () => {
    if(!dateFrom||!dateTo) return;
    setLoading(true); setError('');
    const p = `from=${dateFrom}&to=${dateTo}`;
    try {
      const settled = await Promise.allSettled([
        api.get(`stats/overview?${p}`),
        api.get(`stats/ca-par-jour?${p}`),
        api.get(`stats/top-produits?${p}&limit=10`),
        api.get(`stats/livraisons?${p}`),
        api.get(`stats/statuts?${p}`),
        api.get(`stats/villes?${p}&limit=10`),
        api.get(`stats/conversion?${p}`),
        api.get(`stats/heures?${p}`),
        api.get(`stats/promos?${p}`),
        api.get(`stats/clients-evolution?${p}`),
      ]);
      const ok = (i: number, fb: any) => settled[i].status === 'fulfilled' ? (settled[i] as any).value.data : fb;
      setOverview(ok(0, null)); setCaJour(ok(1, [])); setProduits(ok(2, []));
      setLivraisons(ok(3, [])); setStatuts(ok(4, null)); setVilles(ok(5, []));
      setConversion(ok(6, null)); setHeures(ok(7, [])); setPromos(ok(8, [])); setClients(ok(9, []));
      const failed = settled.filter(r=>r.status==='rejected').length;
      if(failed>0) setError(`${failed} widget(s) n'ont pas pu se charger (endpoints manquants).`);
    } catch { setError('Erreur lors du chargement des statistiques.'); }
    finally { setLoading(false); }
  },[dateFrom,dateTo]);

  useEffect(()=>{ if(dateFrom&&dateTo) loadAll(); },[loadAll]);

  // ── Load compare overview
  useEffect(()=>{
    if(!compareOn||!dateFrom||!dateTo){ setCompareOv(null); return; }
    const cd = getCompareDates(dateFrom,dateTo);
    api.get<Overview>(`stats/overview?from=${cd.from}&to=${cd.to}`)
      .then(r=>setCompareOv(r.data)).catch(()=>setCompareOv(null));
  },[compareOn,dateFrom,dateTo]);

  // ── Drag end
  const handleDragEnd = (result: DropResult) => {
    if(!result.destination) return;
    const next = [...widgetOrder];
    const [rm] = next.splice(result.source.index,1);
    next.splice(result.destination.index,0,rm);
    setWidgetOrder(next);
    localStorage.setItem(ORDER_KEY, JSON.stringify(next));
  };

  // ── Toggle widget visibility
  const toggleWidget = (id: string) => {
    setVisible(prev=>{
      const next = prev.includes(id) ? prev.filter(w=>w!==id) : [...prev,id];
      localStorage.setItem(VISIBLE_KEY, JSON.stringify(next));
      return next;
    });
  };

  // ── Delta badge
  const DeltaBadge = ({curr, prev}: {curr:number; prev:number|undefined}) => {
    if(!compareOn||prev===undefined) return null;
    const d = pct(curr, prev);
    if(d===null) return null;
    return <span className={d>=0?styles.deltaPos:styles.deltaNeg}>{d>=0?'▲':'▼'} {Math.abs(d)}%</span>;
  };

  // ─────────────────────────────────────────────────────────────────
  // Widget renderers
  // ─────────────────────────────────────────────────────────────────

  const renderKPIs = () => overview && (
    <div className={styles.kpiGrid}>
      {([
        {icon:'💶', val:fmt(overview.caTotal),               num:overview.caTotal,            comp:compareOv?.caTotal,            label:"Chiffre d'affaires", cls:'kpiGreen'},
        {icon:'🛒', val:String(overview.nbCommandes),        num:overview.nbCommandes,         comp:compareOv?.nbCommandes,         label:'Commandes payées',   cls:'kpiBlue'},
        {icon:'🧾', val:fmt(overview.panierMoyen),           num:overview.panierMoyen,         comp:compareOv?.panierMoyen,         label:'Panier moyen',       cls:'kpiPurple'},
        {icon:'🎟️',val:`${overview.pctAvecReduction}%`,     num:overview.pctAvecReduction,    comp:compareOv?.pctAvecReduction,    label:'% avec réduction',   cls:'kpiOrange'},
        {icon:'👤', val:String(overview.nbClientsUniques),   num:overview.nbClientsUniques,    comp:compareOv?.nbClientsUniques,    label:'Clients uniques',    cls:'kpiTeal'},
      ] as const).map((k,i)=>(
        <div key={i} className={`${styles.kpiCard} ${styles[k.cls as keyof typeof styles]}`}>
          <div className={styles.kpiIcon}>{k.icon}</div>
          <div className={styles.kpiValue}>{k.val}</div>
          <div className={styles.kpiLabel}>{k.label}</div>
          <DeltaBadge curr={k.num} prev={k.comp} />
          {compareOn && k.comp!==undefined && <div className={styles.kpiCompare}>vs {k.icon==='💶'||k.icon==='🧾'?fmt(k.comp):String(k.comp)}</div>}
        </div>
      ))}
    </div>
  );

  const renderEvolution = () => caJour.length>0 && (
    <div className={styles.chartBox}>
      <ResponsiveContainer width="100%" height={260}>
        <AreaChart data={caJour} margin={{top:10,right:20,left:10,bottom:0}}>
          <defs>
            <linearGradient id="gCA" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#4299e1" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#4299e1" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8"/>
          <XAxis dataKey="date" tickFormatter={fmtShort} tick={{fontSize:11}}/>
          <YAxis tick={{fontSize:11}} tickFormatter={v=>`${v}€`}/>
          <Tooltip formatter={(v:any)=>[fmt(v as number),'CA']} labelFormatter={l=>new Date(l).toLocaleDateString('fr-FR')} contentStyle={{fontSize:12,borderRadius:8}}/>
          <Area type="monotone" dataKey="ca" stroke="#4299e1" strokeWidth={2} fill="url(#gCA)" name="CA"/>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  const renderClients = () => clients.length>0 && (
    <div className={styles.chartBox}>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={clients} margin={{top:5,right:20,left:10,bottom:0}}>
          <defs>
            <linearGradient id="gCL" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor="#48bb78" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#48bb78" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8"/>
          <XAxis dataKey="date" tickFormatter={fmtShort} tick={{fontSize:11}}/>
          <YAxis tick={{fontSize:11}} allowDecimals={false}/>
          <Tooltip formatter={(v:any)=>[v,'Clients']} labelFormatter={l=>new Date(l).toLocaleDateString('fr-FR')} contentStyle={{fontSize:12,borderRadius:8}}/>
          <Area type="monotone" dataKey="nbClients" stroke="#48bb78" strokeWidth={2} fill="url(#gCL)" name="Clients"/>
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );

  const renderConversion = () => conversion && (
    <div className={styles.convBox}>
      <div className={styles.convMain}>
        <div className={styles.convRate}>{conversion.tauxConversion}%</div>
        <div className={styles.convSub}>des commandes aboutissent au paiement</div>
        <div className={styles.convTotal}>{conversion.total} commandes au total</div>
      </div>
      <div className={styles.convBars}>
        {[
          {label:'Payées',     count:conversion.paye,      color:'#48bb78'},
          {label:'En attente', count:conversion.enAttente, color:'#ed8936'},
          {label:'Échec',      count:conversion.echec,     color:'#f56565'},
        ].map((item,i)=>(
          <div key={i} className={styles.convRow}>
            <span className={styles.convLabel}>{item.label}</span>
            <div className={styles.convBarWrap}>
              <div className={styles.convBarFill} style={{width:`${conversion.total>0?item.count/conversion.total*100:0}%`,background:item.color}}/>
            </div>
            <span className={styles.convCount}>{item.count}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderHeures = () => heures.length>0 && (
    <div className={styles.chartBox}>
      <p className={styles.chartNote}>Commandes payées par heure de la journée (sur la période sélectionnée)</p>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={heures} margin={{top:5,right:10,left:0,bottom:0}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" vertical={false}/>
          <XAxis dataKey="heure" tick={{fontSize:10}} tickFormatter={h=>`${h}h`}/>
          <YAxis tick={{fontSize:11}} allowDecimals={false}/>
          <Tooltip formatter={(v:any)=>[v,'Commandes']} labelFormatter={h=>`${h}h00 – ${Number(h)+1}h00`} contentStyle={{fontSize:12,borderRadius:8}}/>
          <Bar dataKey="count" fill="#9f7aea" radius={[3,3,0,0]} name="Commandes"/>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderProduits = () => produits.length>0 && (
    <div className={styles.chartBox}>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={produits} layout="vertical" margin={{top:0,right:20,left:10,bottom:0}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" horizontal={false}/>
          <XAxis type="number" tick={{fontSize:11}}/>
          <YAxis type="category" dataKey="nom" width={130} tick={{fontSize:10}} tickFormatter={v=>v.length>18?v.slice(0,17)+'…':v}/>
          <Tooltip formatter={(v:any,name:any)=>name==='ca'?[fmt(v),'CA']:[v,'Qté vendue']} contentStyle={{fontSize:12,borderRadius:8}}/>
          <Legend wrapperStyle={{fontSize:11}}/>
          <Bar dataKey="quantite" fill="#4299e1" name="Qté vendue" radius={[0,4,4,0]}/>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const renderLivraisons = () => livraisons.length>0 && (
    <div className={styles.chartBox}>
      <ResponsiveContainer width="100%" height={220}>
        <PieChart>
          <Pie data={livraisons} dataKey="count" nameKey="mode" cx="50%" cy="50%" outerRadius={80}
            label={({mode,percent}:any)=>`${MODE_LABELS[mode as string]??mode} ${((percent??0)*100).toFixed(0)}%`}
            labelLine={false}
          >
            {livraisons.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
          </Pie>
          <Tooltip formatter={(v:any,name:any)=>[v,MODE_LABELS[name as string]??name]} contentStyle={{fontSize:12,borderRadius:8}}/>
        </PieChart>
      </ResponsiveContainer>
      <div className={styles.pieDetails}>
        {livraisons.map((lv,i)=>(
          <div key={i} className={styles.pieRow}>
            <span className={styles.pieDot} style={{background:PIE_COLORS[i%PIE_COLORS.length]}}/>
            <span className={styles.pieMode}>{MODE_LABELS[lv.mode]??lv.mode}</span>
            <span className={styles.pieCount}>{lv.count} cmd</span>
            <span className={styles.pieCa}>{fmt(lv.ca)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStatuts = () => statuts && (
    <div className={styles.twoColInner}>
      {([{title:'Paiement',data:statuts.paiement},{title:'Livraison',data:statuts.livraison}]).map(({title,data})=>(
        <div key={title}>
          <div className={styles.subTitle}>{title}</div>
          <div className={styles.chartBox}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={data} dataKey="count" nameKey="statut" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                  {data.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                </Pie>
                <Tooltip formatter={(v:any,n:any)=>[v,STATUT_LABELS[n as string]??n]} contentStyle={{fontSize:12,borderRadius:8}}/>
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.statutList}>
              {data.map((s,i)=>(
                <div key={i} className={styles.statutRow}>
                  <span className={styles.pieDot} style={{background:PIE_COLORS[i%PIE_COLORS.length]}}/>
                  <span>{STATUT_LABELS[s.statut]??s.statut}</span>
                  <span className={styles.statutCount}>{s.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPromos = () => promos.length===0 ? (
    <div className={styles.emptySection}>Aucun code promo utilisé sur cette période.</div>
  ) : (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead><tr><th>Code</th><th>Utilisations</th><th>CA généré</th></tr></thead>
        <tbody>
          {promos.map((p,i)=>(
            <tr key={i}>
              <td><span className={styles.promoCode}>{p.code}</span></td>
              <td>{p.count} fois</td>
              <td className={styles.price}>{fmt(p.ca)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderVilles = () => villes.length>0 && (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead><tr><th>#</th><th>Ville</th><th>Commandes</th><th>CA</th><th>Part</th></tr></thead>
        <tbody>
          {villes.map((v,i)=>{
            const total = villes.reduce((s,x)=>s+x.count,0);
            const p = total>0?Math.round(v.count/total*100):0;
            return (
              <tr key={i}>
                <td className={styles.rank}>{i+1}</td>
                <td className={styles.villeName}>{v.ville}</td>
                <td>{v.count}</td>
                <td className={styles.price}>{fmt(v.ca)}</td>
                <td>
                  <div className={styles.barCell}>
                    <div className={styles.barFill} style={{width:`${p}%`}}/>
                    <span className={styles.barLabel}>{p}%</span>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderContent = (id: string): React.ReactNode => {
    switch(id){
      case 'kpis':       return renderKPIs();
      case 'evolution':  return renderEvolution();
      case 'clients':    return renderClients();
      case 'conversion': return renderConversion();
      case 'heures':     return renderHeures();
      case 'produits':   return renderProduits();
      case 'livraisons': return renderLivraisons();
      case 'statuts':    return renderStatuts();
      case 'promos':     return renderPromos();
      case 'villes':     return renderVilles();
      default:           return null;
    }
  };

  // ─────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className={styles.container}>

        {/* Header */}
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.title}>Statistiques</h1>
            <p className={styles.subtitle}>Analyse des ventes et performances</p>
          </div>
          <div className={styles.headerActions}>
            <button
              className={`${styles.btnCompare} ${compareOn?styles.btnCompareOn:''}`}
              onClick={()=>setCompareOn(p=>!p)}
              title="Comparer avec la période précédente de même durée"
            >
              ↔ {compareOn?'Comparaison ON':'Comparer périodes'}
            </button>
            <button className={styles.btnWidgets} onClick={()=>setShowPanel(p=>!p)}>⚙️ Widgets</button>
            <button className={styles.btnRefresh} onClick={loadAll} disabled={loading}>{loading?'…':'↻'}</button>
          </div>
        </div>

        {/* Compare info banner */}
        {compareOn && dateFrom && dateTo && (()=>{
          const cd = getCompareDates(dateFrom, dateTo);
          return (
            <div className={styles.compareBanner}>
              ↔ Comparaison activée · Période précédente : {new Date(cd.from).toLocaleDateString('fr-FR')} → {new Date(cd.to).toLocaleDateString('fr-FR')}
            </div>
          );
        })()}

        {/* Widget panel */}
        {showPanel && (
          <div className={styles.widgetPanel}>
            <div className={styles.widgetPanelTitle}>Cocher pour afficher · Glisser les widgets pour réordonner</div>
            <div className={styles.widgetList}>
              {ALL_WIDGETS.map(w=>(
                <label key={w.id} className={styles.widgetToggle}>
                  <input type="checkbox" checked={visible.includes(w.id)} onChange={()=>toggleWidget(w.id)}/>
                  <span>{w.icon} {w.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Period bar */}
        <div className={styles.periodBar}>
          <div className={styles.presets}>
            {(['7j','30j','3m','1an'] as Preset[]).map(p=>(
              <button key={p} className={`${styles.presetBtn} ${preset===p?styles.presetActive:''}`} onClick={()=>setPreset(p)}>
                {p==='7j'?'7 jours':p==='30j'?'30 jours':p==='3m'?'3 mois':'1 an'}
              </button>
            ))}
            <button className={`${styles.presetBtn} ${preset==='custom'?styles.presetActive:''}`} onClick={()=>setPreset('custom')}>Personnalisé</button>
          </div>
          {preset==='custom' && (
            <div className={styles.customDates}>
              <input type="date" className={styles.dateInput} value={dateFrom} onChange={e=>setDateFrom(e.target.value)}/>
              <span className={styles.dateSep}>→</span>
              <input type="date" className={styles.dateInput} value={dateTo} onChange={e=>setDateTo(e.target.value)}/>
              <button className={styles.btnApply} onClick={loadAll}>Appliquer</button>
            </div>
          )}
          {dateFrom && dateTo && (
            <div className={styles.periodLabel}>{new Date(dateFrom).toLocaleDateString('fr-FR')} — {new Date(dateTo).toLocaleDateString('fr-FR')}</div>
          )}
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {loading && <div className={styles.loadingBar}><div className={styles.loadingProgress}/></div>}

        {/* Section: Base de données */}
        <div className={styles.sectionDivider}>
          <span className={styles.sectionDividerIcon}>🗄️</span>
          <div>
            <div className={styles.sectionDividerTitle}>Base de données</div>
            <div className={styles.sectionDividerSub}>Commandes, CA, clients, produits vendus — source : votre boutique</div>
          </div>
        </div>

        {/* Draggable widgets */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="stats-widgets">
            {(provided)=>(
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {orderedVisible.map((id, index)=>{
                  const w = ALL_WIDGETS.find(x=>x.id===id);
                  return (
                    <Draggable key={id} draggableId={id} index={index}>
                      {(provided, snapshot)=>(
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={provided.draggableProps.style}
                          className={styles.draggableItem}
                        >
                          <section className={`${styles.section} ${snapshot.isDragging?styles.dragging:''}`}>
                            <div className={styles.sectionHeader}>
                              <div className={styles.sectionLeft}>
                                <span {...provided.dragHandleProps} className={styles.dragHandle} title="Glisser pour déplacer">⠿</span>
                                <h2 className={styles.sectionTitle}>{w?.icon} {w?.label}</h2>
                              </div>
                            </div>
                            {renderContent(id)}
                          </section>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {!loading && overview?.nbCommandes===0 && (
          <div className={styles.emptyState}>Aucune commande payée sur cette période.</div>
        )}

        {/* Section: Google Analytics */}
        <div className={styles.sectionDivider} style={{marginTop:'2rem'}}>
          <span className={styles.sectionDividerIcon}>📈</span>
          <div>
            <div className={styles.sectionDividerTitle}>Google Analytics</div>
            <div className={styles.sectionDividerSub}>Visiteurs, taux de rebond, sources de trafic, pages vues — source : GA4 via Looker Studio</div>
          </div>
        </div>

        {/* GA4 widgets row */}
        <div className={styles.ga4WidgetsRow}>
          {[
            { icon:'👁️',  label:'Pages vues',          sub:'Vues totales sur la boutique' },
            { icon:'👥',  label:'Utilisateurs actifs',  sub:'Visiteurs uniques' },
            { icon:'↩️',  label:'Taux de rebond',       sub:'Visiteurs partis sans cliquer' },
            { icon:'🌐',  label:'Sources de trafic',    sub:'Google, direct, réseaux...' },
          ].map((w,i)=>(
            <div key={i} className={styles.ga4MiniCard}>
              <span className={styles.ga4MiniIcon}>{w.icon}</span>
              <div className={styles.ga4MiniLabel}>{w.label}</div>
              <div className={styles.ga4MiniSub}>{w.sub}</div>
            </div>
          ))}
        </div>

        <div className={styles.ga4Section}>
          <div className={styles.ga4SectionHeader}>
            <span>📊 Rapport Google Analytics 4 — période sélectionnée</span>
            <a
              href={`https://analytics.google.com`}
              target="_blank" rel="noreferrer"
              className={styles.ga4ExtLink}
            >Ouvrir GA4 ↗</a>
          </div>
          <iframe
            key={`ga4-${dateFrom}-${dateTo}`}
            className={styles.ga4Frame}
            src={`https://lookerstudio.google.com/embed/reporting/${GA4_REPORT_ID}/page/kIV1C${dateFrom && dateTo ? `?_u.date00=${dateFrom.replace(/-/g,'')}&_u.date01=${dateTo.replace(/-/g,'')}` : ''}`}
            frameBorder="0"
            allowFullScreen
            sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          />
        </div>

      </div>
    </Layout>
  );
};
